import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShieldCheck,
  Check,
  X,
  ZoomIn,
  FileText,
  User,
  Clock,
} from 'lucide-react';

export const Route = createFileRoute('/_admin/kyc/review/$id')({
  component: KycReviewDetailPage,
});

interface KycSubmission {
  id: string;
  user_id: string;
  doc_type: string;
  doc_front_url: string;
  doc_back_url: string;
  selfie_url: string;
  proof_of_residence_url: string;
  status: string;
  rejection_reason: string;
  reviewed_by_admin_id: string;
  reviewed_at: string;
  created_at: string;
  updated_at: string;
  user_full_name_masked: string;
  user_cpf_masked: string;
}

function mapSubmission(s: any): KycSubmission {
  return {
    id: s.id ?? '',
    user_id: s.userId ?? s.user_id ?? '',
    doc_type: s.docType ?? s.doc_type ?? '',
    doc_front_url: s.docFrontUrl ?? s.doc_front_url ?? '',
    doc_back_url: s.docBackUrl ?? s.doc_back_url ?? '',
    selfie_url: s.selfieUrl ?? s.selfie_url ?? '',
    proof_of_residence_url: s.proofOfResidenceUrl ?? s.proof_of_residence_url ?? '',
    status: s.status ?? 'pending',
    rejection_reason: s.rejectionReason ?? s.rejection_reason ?? '',
    reviewed_by_admin_id: s.reviewedByAdminId ?? s.reviewed_by_admin_id ?? '',
    reviewed_at: s.reviewedAt ?? s.reviewed_at ?? '',
    created_at: s.createdAt ?? s.created_at ?? '',
    updated_at: s.updatedAt ?? s.updated_at ?? '',
    user_full_name_masked: s.userFullNameMasked ?? s.user_full_name_masked ?? '--',
    user_cpf_masked: s.userCpfMasked ?? s.user_cpf_masked ?? '--',
  };
}

function formatDate(iso: string): string {
  if (!iso) return '--';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'pending':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pendente</Badge>;
    case 'approved':
      return <Badge variant="outline" className="border-green-500 text-green-600">Aprovado</Badge>;
    case 'rejected':
      return <Badge variant="outline" className="border-destructive text-destructive">Rejeitado</Badge>;
    case 'additional_info_required':
      return <Badge variant="outline" className="border-orange-500 text-orange-500">Info Adicional</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

interface DocPreviewProps {
  url: string;
  label: string;
}

function DocPreview({ url, label }: DocPreviewProps) {
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!url) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border bg-muted h-48 gap-2 text-muted-foreground text-sm">
        <FileText className="h-8 w-8 opacity-40" />
        <span>{label}</span>
        <span className="text-xs">Nao enviado</span>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div
          className="relative rounded-md border overflow-hidden cursor-zoom-in group"
          onClick={() => setZoomOpen(true)}
        >
          <img
            src={url}
            alt={label}
            className="w-full h-48 object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
            <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </div>

      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh] flex items-center justify-center">
            <img
              src={url}
              alt={label}
              className="max-w-full max-h-full object-contain rounded"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZoomOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KycReviewDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const { data: submission, isLoading, error } = useQuery<KycSubmission>({
    queryKey: ['admin-kyc-submission', id],
    queryFn: async () => {
      const res = await api.get(`/admin/kyc/${id}`);
      return mapSubmission(res.data);
    },
    retry: 1,
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ approve, reason }: { approve: boolean; reason?: string }) =>
      api.post(`/admin/kyc/${id}/review`, {
        approve,
        rejection_reason: reason ?? '',
      }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-kyc-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-kyc-submission', id] });
      if (vars.approve) {
        toast.success('Submissao KYC aprovada com sucesso.');
      } else {
        toast.success('Submissao KYC rejeitada.');
      }
      setApproveOpen(false);
      setRejectOpen(false);
      setRejectReason('');
      navigate({ to: '/kyc/review' });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao processar revisao KYC');
    },
  });

  const isPending = submission?.status === 'pending' || submission?.status === 'additional_info_required';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        Carregando...
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="space-y-4">
        <Link to="/kyc/review">
          <Button variant="ghost" size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
        </Link>
        <p className="text-destructive text-sm">Submissao nao encontrada ou erro ao carregar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Link to="/kyc/review">
          <Button variant="ghost" size="sm" className="gap-1">
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold">Revisao KYC</h1>
          {statusBadge(submission.status)}
        </div>
      </div>

      {/* Dados do usuario */}
      <div className="rounded-md border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <User className="h-4 w-4" />
          Dados do Usuario
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">Nome</p>
            <p className="font-medium">{submission.user_full_name_masked}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">CPF</p>
            <p className="font-mono">{submission.user_cpf_masked}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">Tipo de Documento</p>
            <Badge variant="secondary" className="text-xs uppercase">{submission.doc_type || '--'}</Badge>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-muted-foreground">ID da Submissao</p>
            <p className="font-mono text-xs text-muted-foreground break-all">{submission.id}</p>
          </div>
        </div>
      </div>

      {/* Preview dos documentos */}
      <div className="space-y-3">
        <p className="text-sm font-semibold">Documentos Enviados</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DocPreview url={submission.doc_front_url} label="Documento — Frente" />
          <DocPreview url={submission.doc_back_url} label="Documento — Verso" />
          <DocPreview url={submission.selfie_url} label="Selfie" />
          {submission.proof_of_residence_url && (
            <DocPreview url={submission.proof_of_residence_url} label="Comprovante de Residencia" />
          )}
        </div>
      </div>

      {/* Historico de revisao */}
      {(submission.reviewed_at || submission.rejection_reason) && (
        <div className="rounded-md border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4" />
            Historico de Revisao
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {submission.reviewed_at && (
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Revisado em</p>
                <p>{formatDate(submission.reviewed_at)}</p>
              </div>
            )}
            {submission.reviewed_by_admin_id && (
              <div className="space-y-0.5">
                <p className="text-xs text-muted-foreground">Revisado por</p>
                <p className="font-mono text-xs">{submission.reviewed_by_admin_id}</p>
              </div>
            )}
            <div className="space-y-0.5">
              <p className="text-xs text-muted-foreground">Enviado em</p>
              <p>{formatDate(submission.created_at)}</p>
            </div>
            {submission.rejection_reason && (
              <div className="col-span-2 md:col-span-3 space-y-0.5">
                <p className="text-xs text-muted-foreground">Motivo da Rejeicao</p>
                <p className="text-destructive">{submission.rejection_reason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Acoes */}
      {isPending && (
        <div className="flex items-center gap-3 pt-2">
          <Button
            className="bg-green-600 hover:bg-green-700 text-white gap-2"
            onClick={() => setApproveOpen(true)}
            disabled={reviewMutation.isPending}
          >
            <Check className="h-4 w-4" />
            Aprovar
          </Button>
          <Button
            variant="outline"
            className="text-destructive border-destructive hover:bg-destructive/10 gap-2"
            onClick={() => {
              setRejectReason('');
              setRejectOpen(true);
            }}
            disabled={reviewMutation.isPending}
          >
            <X className="h-4 w-4" />
            Rejeitar
          </Button>
        </div>
      )}

      {/* Modal approve */}
      <ConfirmModal
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={() => reviewMutation.mutate({ approve: true })}
        title="Aprovar KYC"
        description={`Confirma aprovacao do KYC de ${submission.user_full_name_masked}? O nivel de KYC do usuario sera atualizado e o audit log registrado.`}
        confirmText={reviewMutation.isPending ? 'Aprovando...' : 'Aprovar'}
        cancelText="Cancelar"
      />

      {/* Modal reject */}
      <Dialog
        open={rejectOpen}
        onOpenChange={(open) => {
          if (!open) { setRejectOpen(false); setRejectReason(''); }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeitar KYC</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Informe o motivo da rejeicao. O usuario sera notificado.
            </p>
            <div className="space-y-2">
              <Label htmlFor="kyc-reject-reason">Motivo *</Label>
              <Textarea
                id="kyc-reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Documento ilegivel, selfie nao confere com documento..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setRejectOpen(false); setRejectReason(''); }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || reviewMutation.isPending}
              onClick={() => {
                if (rejectReason.trim()) {
                  reviewMutation.mutate({ approve: false, reason: rejectReason.trim() });
                }
              }}
            >
              {reviewMutation.isPending ? 'Rejeitando...' : 'Rejeitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
